/**
 * Validation for the employee self-service profile.
 *
 * Deliberately a PURE function over the whole profile rather than per-input handlers: the same
 * rules have to run in the browser (so the employee gets a usable message) and again on the
 * server (so they cannot be bypassed by posting at the API). A pure function is the only shape
 * that lifts into a Lambda unchanged — the same reasoning attendanceEngine.ts applies, and the
 * reason the phone/Aadhaar/PAN normalizers already exist as standalone modules.
 *
 * Returns every problem rather than the first: somebody filling eleven sections should not
 * discover their mistakes one submit at a time.
 */

import { EmployeeSelfServiceProfile } from '../types/employeeProfile';
import { normalizeAadhaar, normalizePan } from '../utils/identityDocs';
import { normalizePhoneE164 } from '../utils/phone';

/** A problem, tagged with the section it belongs to so the page can show it in context. */
export interface ProfileValidationIssue {
  section: string;
  message: string;
}

const isBlank = (value?: string): boolean => !String(value ?? '').trim();

/** YYYY-MM-DD and a real calendar date — "2026-02-31" parses in JS and must not pass. */
const isIsoDate = (value?: string): boolean => {
  const raw = String(value ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false;
  const parsed = new Date(raw + 'T00:00:00Z');
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === raw;
};

const isEmail = (value?: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? '').trim());

/** Six digits. Indian PIN codes never start with 0. */
const isPincode = (value?: string): boolean => /^[1-9]\d{5}$/.test(String(value ?? '').trim());

/** Standard IFSC: four letters, a 0, then six alphanumerics. */
const isIfsc = (value?: string): boolean =>
  /^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(value ?? '').trim().toUpperCase());

/**
 * Validate a profile.
 *
 * `draft` runs the same format rules WITHOUT the required-field checks. A partial save is a
 * legitimate stored state (IN_PROGRESS) and refusing it would mean an employee interrupted
 * halfway loses everything — but what is present is still checked, because storing a malformed
 * Aadhaar and only reporting it at submit is worse than rejecting it at save.
 */
export function validateEmployeeProfile(
  profile: Partial<EmployeeSelfServiceProfile>,
  mode: 'draft' | 'submit' = 'submit'
): ProfileValidationIssue[] {
  const issues: ProfileValidationIssue[] = [];
  const required = mode === 'submit';
  const add = (section: string, message: string) => issues.push({ section, message });

  // ── Personal ────────────────────────────────────────────────────────────────────────
  const personal = profile.personal || {};
  if (required && isBlank(personal.dateOfBirth)) add('personal', 'Date of birth is required.');
  if (!isBlank(personal.dateOfBirth) && !isIsoDate(personal.dateOfBirth)) {
    add('personal', 'Date of birth must be a valid date.');
  } else if (!isBlank(personal.dateOfBirth)) {
    /*
     * A sanity bound on the year, not an HR policy.
     *
     * It exists to catch a mistyped year, which is the common error on this field and otherwise
     * passes silently — surfacing years later as a wrong retirement date or a failed KYC.
     */
    const year = Number(String(personal.dateOfBirth).slice(0, 4));
    const thisYear = new Date().getFullYear();
    if (year > thisYear - 14 || year < thisYear - 100) {
      add('personal', 'Date of birth looks wrong — check the year.');
    }
  }
  if (required && isBlank(personal.gender)) add('personal', 'Gender is required.');
  if (required && isBlank(personal.mobileNumber)) add('personal', 'Mobile number is required.');
  if (!isBlank(personal.mobileNumber) && normalizePhoneE164(personal.mobileNumber) === null) {
    add('personal', 'Mobile number must include the country code, for example +919876543210.');
  }
  if (!isBlank(personal.personalEmail) && !isEmail(personal.personalEmail)) {
    add('personal', 'Personal email is not a valid email address.');
  }
  if (required && isBlank(personal.fathersName)) add('personal', 'Father name is required.');
  // Married with no spouse name is the inconsistency worth catching; the reverse is not.
  if (
    String(personal.maritalStatus || '').toLowerCase() === 'married' &&
    isBlank(personal.spouseName)
  ) {
    add('personal', 'Spouse name is required when marital status is Married.');
  }
  if (!isBlank(personal.marriageDate) && !isIsoDate(personal.marriageDate)) {
    add('personal', 'Marriage date must be a valid date.');
  }

  // ── Address ─────────────────────────────────────────────────────────────────────────
  const permanent = profile.address?.permanent || {};
  if (required && isBlank(permanent.line1)) add('address', 'Permanent address is required.');
  if (required && isBlank(permanent.city)) add('address', 'Permanent address city is required.');
  if (required && isBlank(permanent.state)) add('address', 'Permanent address state is required.');
  if (required && isBlank(permanent.pincode)) {
    add('address', 'Permanent address PIN code is required.');
  }
  if (!isBlank(permanent.pincode) && !isPincode(permanent.pincode)) {
    add('address', 'Permanent address PIN code must be 6 digits.');
  }
  const present = profile.address?.present || {};
  if (!isBlank(present.pincode) && !isPincode(present.pincode)) {
    add('address', 'Present address PIN code must be 6 digits.');
  }

  // ── Emergency contact ───────────────────────────────────────────────────────────────
  const emergency = profile.emergencyContact || {};
  if (required && isBlank(emergency.name)) {
    add('emergencyContact', 'Emergency contact name is required.');
  }
  if (required && isBlank(emergency.mobileNumber)) {
    add('emergencyContact', 'Emergency contact number is required.');
  }
  if (!isBlank(emergency.mobileNumber) && normalizePhoneE164(emergency.mobileNumber) === null) {
    add('emergencyContact', 'Emergency contact number must include the country code.');
  }
  if (required && isBlank(emergency.relationship)) {
    add('emergencyContact', 'Emergency contact relationship is required.');
  }
  /*
   * The emergency contact must not be the employee themselves.
   *
   * Pasting your own number here defeats the only purpose the field has, and on a long form it
   * is an easy slip. Compared normalized, so "+91 98765 43210" and "+919876543210" are seen as
   * the same number rather than as two different ones.
   */
  if (
    !isBlank(emergency.mobileNumber) &&
    !isBlank(personal.mobileNumber) &&
    normalizePhoneE164(emergency.mobileNumber) === normalizePhoneE164(personal.mobileNumber)
  ) {
    add('emergencyContact', 'Emergency contact number cannot be your own mobile number.');
  }

  // ── Identifications ─────────────────────────────────────────────────────────────────
  const ids = profile.identifications || {};
  if (required && isBlank(ids.aadhaarNumber)) {
    add('identifications', 'Aadhaar number is required.');
  }
  if (!isBlank(ids.aadhaarNumber) && normalizeAadhaar(ids.aadhaarNumber) === null) {
    add('identifications', 'Aadhaar number must be exactly 12 digits.');
  }
  if (required && isBlank(ids.panNumber)) add('identifications', 'PAN is required.');
  if (!isBlank(ids.panNumber) && normalizePan(ids.panNumber) === null) {
    add('identifications', 'PAN must be in the format ABCDE1234F.');
  }
  if (!isBlank(ids.passportExpiry) && !isIsoDate(ids.passportExpiry)) {
    add('identifications', 'Passport expiry must be a valid date.');
  }
  if (required && !(ids.aadhaarDocumentKeys || []).length) {
    add('identifications', 'Attach a copy of your Aadhaar card.');
  }
  if (required && !(ids.panDocumentKeys || []).length) {
    add('identifications', 'Attach a copy of your PAN card.');
  }

  // ── Qualifications ──────────────────────────────────────────────────────────────────
  if (required && !(profile.qualifications || []).length) {
    add('qualifications', 'Add at least one qualification.');
  }
  (profile.qualifications || []).forEach((row, index) => {
    const label = 'Qualification ' + (index + 1);
    if (isBlank(row.qualification)) add('qualifications', label + ': qualification is required.');
    if (isBlank(row.institute)) add('qualifications', label + ': institute is required.');
    if (required && !(row.documentKeys || []).length) {
      add('qualifications', label + ': attach the certificate.');
    }
    if (row.fromYear && row.toYear && Number(row.toYear) < Number(row.fromYear)) {
      add('qualifications', label + ': the To year cannot be before the From year.');
    }
  });

  // ── Previous employment ─────────────────────────────────────────────────────────────
  (profile.previousEmployment || []).forEach((row, index) => {
    const label = 'Previous employment ' + (index + 1);
    if (isBlank(row.companyName)) add('previousEmployment', label + ': company name is required.');
    if (!isBlank(row.fromDate) && !isIsoDate(row.fromDate)) {
      add('previousEmployment', label + ': From date must be a valid date.');
    }
    if (!isBlank(row.toDate) && !isIsoDate(row.toDate)) {
      add('previousEmployment', label + ': To date must be a valid date.');
    }
    if (
      isIsoDate(row.fromDate) &&
      isIsoDate(row.toDate) &&
      String(row.toDate) < String(row.fromDate)
    ) {
      add('previousEmployment', label + ': the To date cannot be before the From date.');
    }
  });

  // ── Family ──────────────────────────────────────────────────────────────────────────
  (profile.family || []).forEach((row, index) => {
    const label = 'Family member ' + (index + 1);
    if (isBlank(row.name)) add('family', label + ': name is required.');
    if (isBlank(row.relationship)) add('family', label + ': relationship is required.');
    if (!isBlank(row.dateOfBirth) && !isIsoDate(row.dateOfBirth)) {
      add('family', label + ': date of birth must be a valid date.');
    }
  });

  // ── Nominations ─────────────────────────────────────────────────────────────────────
  const nominations = profile.nominations || [];
  nominations.forEach((row, index) => {
    const label = 'Nominee ' + (index + 1);
    if (isBlank(row.nomineeName)) add('nominations', label + ': name is required.');
    if (isBlank(row.relationship)) add('nominations', label + ': relationship is required.');
    const share = Number(row.sharePercentage);
    if (!isBlank(row.sharePercentage) && (!Number.isFinite(share) || share <= 0 || share > 100)) {
      add('nominations', label + ': share must be between 1 and 100.');
    }
  });
  /*
   * Shares must total exactly 100 whenever anybody is nominated.
   *
   * A nomination splitting 60/30 is not a smaller version of a valid one, it is undefined — and
   * the ambiguity surfaces when the benefit is being paid out, which is the worst possible
   * moment to find it. Only checked when rows exist: nominating nobody is allowed.
   */
  if (nominations.length > 0) {
    const total = nominations.reduce((sum, row) => sum + (Number(row.sharePercentage) || 0), 0);
    if (Math.round(total) !== 100) {
      add('nominations', 'Nominee shares must total 100%. They currently total ' + total + '%.');
    }
  }

  // ── Accounts & statutory ────────────────────────────────────────────────────────────
  const accounts = profile.accounts || {};
  if (required && isBlank(accounts.accountNumber)) {
    add('accounts', 'Bank account number is required.');
  }
  if (
    !isBlank(accounts.accountNumber) &&
    !/^\d{9,18}$/.test(String(accounts.accountNumber).trim())
  ) {
    add('accounts', 'Bank account number must be 9 to 18 digits.');
  }
  if (required && isBlank(accounts.ifscCode)) add('accounts', 'IFSC code is required.');
  if (!isBlank(accounts.ifscCode) && !isIfsc(accounts.ifscCode)) {
    add('accounts', 'IFSC code must be in the format ABCD0123456.');
  }
  if (!isBlank(accounts.uanNumber) && !/^\d{12}$/.test(String(accounts.uanNumber).trim())) {
    add('accounts', 'UAN must be 12 digits.');
  }

  return issues;
}

/** Issues grouped by section, for rendering each under the section it belongs to. */
export const groupIssues = (issues: ProfileValidationIssue[]): Record<string, string[]> =>
  issues.reduce((acc, issue) => {
    (acc[issue.section] = acc[issue.section] || []).push(issue.message);
    return acc;
  }, {} as Record<string, string[]>);
